/**
 * Filter und Gruppierung der E-Mail-Vorlagen – gemeinsam genutzt vom
 * Auswahlfenster „Vorlage einfügen“ im E-Mail-Fenster und von der
 * Vorlagenverwaltung (Kundenfeedback Kanzlei Polz 25.10.2026 Teil 2,
 * II.–IV.):
 *
 *  - Vorauswahl „Unternehmensvorlagen“ (Reichweite Organisation oder
 *    Unternehmen) gegenüber „Eigene Vorlagen“ (persönlich) als Doppelpille.
 *  - Vorauswahl, an wen die Nachricht geht. Die vorhandenen Kategorien
 *    (Versicherung, Mandant / Kunde, Werkstatt, Sachverständiger, …) sind
 *    zugleich die Empfänger – ein eigenes Datenfeld ist nicht nötig.
 *  - Deep-Link in die Vorlagenverwaltung (?bereich=templates), damit
 *    „Vorlagen verwalten“ direkt dort landet.
 *
 * Reine Funktionen ohne Vue.
 */

import type { MailTemplate, MailTemplateCaseType, MailTemplateCategory, MailTemplateScope } from '@/services/mailTemplatesApi'
import type { RecipientCandidate } from '@/utils/mailRecipientCandidates'
import { gtKey } from '@/i18n/gt'
import { htmlToPreviewText } from '@/utils/mailTemplate'

/** Vorauswahl der Doppelpille: Unternehmens- bzw. eigene Vorlagen. */
export type TemplateOwner = 'shared' | 'own'

export interface TemplateOwnerOption {
  value: TemplateOwner
  label: string
  /** Erklärung als Tooltip an der Pille. */
  hint: string
}

export const TEMPLATE_OWNER_OPTIONS: readonly TemplateOwnerOption[] = [
  {
    value: 'shared',
    label: gtKey('Unternehmensvorlagen'),
    hint: gtKey('Vorlagen Ihrer Kanzlei für alle Mitarbeitenden – gepflegt von den Administratoren'),
  },
  {
    value: 'own',
    label: gtKey('Eigene Vorlagen'),
    hint: gtKey('Ihre persönlichen Vorlagen – nur Sie sehen und bearbeiten sie'),
  },
] as const

/** Kategorie „Sonstiges“: Sammelgruppe für unbekannte Kategorien. */
const OTHER_CATEGORY = 'sonstiges'

/** Länge des durchsuchten Vorlagentexts (ohne HTML). */
const SEARCH_TEXT_LENGTH = 4000

/** localStorage-Schlüssel der zuletzt gewählten Doppelpille. */
export const OWNER_PREFERENCE_KEY = 'kars.mailTemplates.owner'

export function templateOwner (t: Pick<MailTemplate, 'scope'>): TemplateOwner {
  return t.scope === 'personal' ? 'own' : 'shared'
}

/** Reichweite, mit der eine neue Vorlage in der gewählten Ansicht angelegt wird. */
export function scopeForOwner (owner: TemplateOwner, canManageShared: boolean): MailTemplateScope {
  return owner === 'shared' && canManageShared ? 'organization' : 'personal'
}

export function templateMatchesQuery (t: MailTemplate, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) {
    return true
  }
  return t.name.toLowerCase().includes(q)
    || (t.subject || '').toLowerCase().includes(q)
    || htmlToPreviewText(t.bodyHtml, SEARCH_TEXT_LENGTH).toLowerCase().includes(q)
}

export interface TemplateFilter {
  /** null = beide Bereiche. */
  owner: TemplateOwner | null
  /** Kategorie-Schlüssel; leer = alle Empfänger. */
  category: string
  query: string
  /** Inaktive Vorlagen ausblenden (Auswahlfenster). */
  activeOnly?: boolean
}

export function filterMailTemplates (list: readonly MailTemplate[], filter: TemplateFilter): MailTemplate[] {
  return list.filter(t => {
    if (filter.activeOnly && !t.isActive) {
      return false
    }
    if (filter.owner && templateOwner(t) !== filter.owner) {
      return false
    }
    if (filter.category && t.category !== filter.category) {
      return false
    }
    return templateMatchesQuery(t, filter.query)
  })
}

// ── Fallart (Kasko-Schreiben, Mail RA Polz 13.10.2026) ─────────────────

/** Fallart einer Vorlage bzw. Akte; Unbekanntes gilt als „alle Fallarten“. */
export function normalizeCaseType (value: unknown): MailTemplateCaseType {
  const v = String(value ?? '').trim().toLowerCase()
  return v === 'kasko' || v === 'haftpflicht' ? v : ''
}

/** Gruppen-Schlüssel der vorgefertigten Kasko-Schreiben im Auswahlfenster. */
export const KASKO_GROUP_KEY = '__kasko'

/**
 * Passt die Vorlage zur Akte? Vorlagen ohne Fallart passen immer; ohne
 * Akte (oder Fallart unbekannt) wird alles angeboten.
 */
export function templateFitsCaseType (t: Pick<MailTemplate, 'caseType'>, caseType: unknown): boolean {
  const own = normalizeCaseType(t.caseType)
  const target = normalizeCaseType(caseType)
  return !own || !target || own === target
}

export function filterByCaseType (list: readonly MailTemplate[], caseType: unknown): MailTemplate[] {
  return list.filter(t => templateFitsCaseType(t, caseType))
}

/** Aktive Kasko-Schreiben (geteilt oder eigen). */
export function kaskoTemplates (list: readonly MailTemplate[]): MailTemplate[] {
  return list.filter(t => t.isActive && normalizeCaseType(t.caseType) === 'kasko')
}

export interface TemplateGroup {
  key: string
  label: string
  items: MailTemplate[]
}

/**
 * Gruppen in Kategorie-Reihenfolge (nur nicht leere). Unbekannte Kategorien
 * landen unter „Sonstiges“ – bzw. in einer eigenen Restgruppe, wenn es die
 * Kategorie nicht gibt.
 */
export function groupTemplatesByCategory (list: readonly MailTemplate[], categories: readonly MailTemplateCategory[]): TemplateGroup[] {
  const known = new Set(categories.map(c => c.key))
  const hasOther = known.has(OTHER_CATEGORY)
  const groups: TemplateGroup[] = categories.map(c => ({
    key: c.key,
    label: c.label,
    items: list.filter(t => t.category === c.key || (c.key === OTHER_CATEGORY && !known.has(t.category))),
  }))
  const rest = hasOther ? [] : list.filter(t => !known.has(t.category))
  const restGroup: TemplateGroup[] = rest.length > 0
    ? [{ key: '__other', label: gtKey('Sonstiges'), items: rest }]
    : []
  return [...groups.filter(g => g.items.length > 0), ...restGroup]
}

/**
 * Gruppen für das Auswahlfenster: In einer Kasko-Akte stehen die
 * Kasko-Schreiben als eigene, erste Gruppe („im Kaskobereich muss sehr klar
 * sein, dass wir hier vorgefertigte Schreiben haben“), danach die übrigen
 * Vorlagen nach Empfänger.
 */
export function groupTemplatesForCase (
  list: readonly MailTemplate[],
  categories: readonly MailTemplateCategory[],
  caseType: unknown,
): TemplateGroup[] {
  if (normalizeCaseType(caseType) !== 'kasko') {
    return groupTemplatesByCategory(list, categories)
  }
  const kasko = list.filter(t => normalizeCaseType(t.caseType) === 'kasko')
  const rest = list.filter(t => normalizeCaseType(t.caseType) !== 'kasko')
  const kaskoGroup: TemplateGroup[] = kasko.length > 0
    ? [{ key: KASKO_GROUP_KEY, label: gtKey('Kasko-Schreiben'), items: kasko }]
    : []
  return [...kaskoGroup, ...groupTemplatesByCategory(rest, categories)]
}

export function countByOwner (list: readonly MailTemplate[], opts: { activeOnly?: boolean } = {}): Record<TemplateOwner, number> {
  const activeOnly = opts.activeOnly ?? true
  return list.reduce<Record<TemplateOwner, number>>((acc, t) => {
    if (activeOnly && !t.isActive) {
      return acc
    }
    const owner = templateOwner(t)
    return { ...acc, [owner]: acc[owner] + 1 }
  }, { shared: 0, own: 0 })
}

/**
 * Startansicht der Doppelpille: die gemerkte Wahl, solange dort (aktive)
 * Vorlagen liegen; sonst der Bereich, in dem es Vorlagen gibt.
 */
export function resolveOwner (list: readonly MailTemplate[], preferred: TemplateOwner | null, caseType: unknown = ''): TemplateOwner {
  // Kasko-Akte: die vorgefertigten Kasko-Schreiben der Kanzlei zuerst zeigen.
  if (normalizeCaseType(caseType) === 'kasko' && kaskoTemplates(list).some(t => templateOwner(t) === 'shared')) {
    return 'shared'
  }
  const counts = countByOwner(list)
  if (preferred && counts[preferred] > 0) {
    return preferred
  }
  if (counts.shared > 0) {
    return 'shared'
  }
  if (counts.own > 0) {
    return 'own'
  }
  return preferred ?? 'shared'
}

export function parseOwnerPreference (value: unknown): TemplateOwner | null {
  return value === 'shared' || value === 'own' ? value : null
}

/** Zuletzt gewählte Doppelpille (je Browser); Fehler beim Speicherzugriff → keine Vorgabe. */
export function readOwnerPreference (): TemplateOwner | null {
  try {
    return parseOwnerPreference(globalThis.localStorage?.getItem(OWNER_PREFERENCE_KEY))
  } catch {
    return null
  }
}

export function writeOwnerPreference (owner: TemplateOwner): void {
  try {
    globalThis.localStorage?.setItem(OWNER_PREFERENCE_KEY, owner)
  } catch {
    /* Privatmodus o. ä.: Auswahl gilt nur für diese Sitzung */
  }
}

/** Rollen-Stichwörter → Kategorie (Reihenfolge = Vorrang). */
const ROLE_CATEGORY_RULES: ReadonlyArray<{ pattern: RegExp, category: string }> = [
  { pattern: /versicherung|regulierung|schutzbrief/i, category: 'versicherung' },
  { pattern: /werkstatt|autohaus/i, category: 'werkstatt' },
  { pattern: /sachverständig|sachverstaendig|gutacht/i, category: 'sachverstaendiger' },
  { pattern: /kunde|mandant/i, category: 'mandant' },
]

/**
 * Vorlagen-Kategorie passend zum Empfänger der Nachricht (Kunde →
 * „Mandant / Kunde“, Versicherung → „Versicherung“, …); leer, wenn sich
 * nichts ableiten lässt.
 */
export function categoryForRecipient (candidate: Pick<RecipientCandidate, 'group' | 'role'> | null | undefined): string {
  if (!candidate || candidate.group === 'team') {
    return ''
  }
  if (candidate.group === 'client') {
    return 'mandant'
  }
  if (candidate.group === 'insurance') {
    return 'versicherung'
  }
  return ROLE_CATEGORY_RULES.find(r => r.pattern.test(candidate.role || ''))?.category ?? ''
}

/**
 * Start-Empfänger im Auswahlfenster: der Vorschlag aus dem Empfänger der
 * Nachricht, sofern es dafür im gewählten Bereich aktive Vorlagen gibt –
 * sonst „Alle“, damit das Fenster nicht leer öffnet.
 */
export function initialCategory (
  list: readonly MailTemplate[],
  categories: readonly MailTemplateCategory[],
  owner: TemplateOwner,
  suggested: string,
): string {
  if (!suggested || !categories.some(c => c.key === suggested)) {
    return ''
  }
  const hits = filterMailTemplates(list, { owner, category: suggested, query: '', activeOnly: true })
  return hits.length > 0 ? suggested : ''
}

// ── Deep-Link in die Vorlagenverwaltung ─────────────────────────────────

/** Bereichs-Schlüssel der Kachel „E-Mail-Vorlagen“ in den Kommunikationseinstellungen. */
export const TEMPLATE_MANAGER_AREA = 'templates'

/** Abfrage-Parameter, die nur zur Vorlagenverwaltung gehören. */
export const TEMPLATE_MANAGER_QUERY_KEYS = ['ansicht', 'empfaenger', 'neu'] as const

const OWNER_TO_QUERY: Record<TemplateOwner, string> = { shared: 'unternehmen', own: 'eigene' }

export interface TemplateManagerLink {
  owner?: TemplateOwner | null
  category?: string
  /** Editor für eine neue Vorlage direkt öffnen. */
  create?: boolean
}

export function templateManagerQuery (link: TemplateManagerLink): Record<string, string> {
  return {
    bereich: TEMPLATE_MANAGER_AREA,
    ...(link.owner ? { ansicht: OWNER_TO_QUERY[link.owner] } : {}),
    ...(link.category ? { empfaenger: link.category } : {}),
    ...(link.create ? { neu: '1' } : {}),
  }
}

export interface ParsedTemplateManagerLink {
  owner: TemplateOwner | null
  category: string
  create: boolean
}

export function parseTemplateManagerQuery (query: Record<string, unknown>): ParsedTemplateManagerLink {
  const ansicht = query.ansicht
  const owner = (Object.keys(OWNER_TO_QUERY) as TemplateOwner[]).find(o => OWNER_TO_QUERY[o] === ansicht) ?? null
  const category = typeof query.empfaenger === 'string' && /^[a-z_]{1,40}$/.test(query.empfaenger)
    ? query.empfaenger
    : ''
  return { owner, category, create: query.neu === '1' }
}
